import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  getCurrentOrganization,
  OutputType as CurrentOrgOutput,
} from "../endpoints/organizations/current_GET.schema";
import {
  getOrganizationMembers,
  OutputType as MembersOutput,
} from "../endpoints/organizations/members_GET.schema";
import { useAuth } from "./useAuth";
import { UserRole } from "./schema";

// Define types based on endpoint outputs
export type Organization = NonNullable<CurrentOrgOutput["organization"]>;
export type OrganizationMember = MembersOutput["members"][number];

interface OrganizationContextType {
  organization: Organization | null;
  members: OrganizationMember[];
  isLoading: boolean;
  isMembersLoading: boolean;
  error: Error | null;
  refetchOrganization: () => Promise<void>;
  refetchMembers: () => Promise<void>;
  isOwner: boolean;
  isAdmin: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined
);

export const ORGANIZATION_QUERY_KEY = ["organization", "current"] as const;
export const MEMBERS_QUERY_KEY = ["organization", "members"] as const;

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const isAuthenticated = authState.type === "authenticated";

  // Fetch current organization
  const {
    data: orgData,
    isLoading: isOrgLoading,
    error: orgError,
    refetch: refetchOrg,
  } = useQuery({
    queryKey: ORGANIZATION_QUERY_KEY,
    queryFn: async () => {
      const result = await getCurrentOrganization();
      return result.organization;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch members if we have an organization
  const {
    data: membersData,
    isLoading: isMembersLoading,
    error: membersError,
    refetch: refetchMem,
  } = useQuery({
    queryKey: MEMBERS_QUERY_KEY,
    queryFn: async () => {
      const result = await getOrganizationMembers();
      return result.members;
    },
    enabled: isAuthenticated && !!orgData,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const refetchOrganization = useCallback(async () => {
    await refetchOrg();
  }, [refetchOrg]);

  const refetchMembers = useCallback(async () => {
    await refetchMem();
  }, [refetchMem]);

  // Determine roles
  const currentUserId =
    authState.type === "authenticated" ? authState.user.id : null;
  
  const isOwner =
    !!orgData && !!currentUserId && orgData.ownerUserId === currentUserId;
    
  // Check if current user is admin in the members list
  const currentUserMember = membersData?.find(
    (m) => m.user.id === currentUserId
  );
  const isAdmin =
    isOwner || (!!currentUserMember && currentUserMember.role === "admin");

  const value: OrganizationContextType = {
    organization: orgData ?? null,
    members: membersData ?? [],
    isLoading: isOrgLoading,
    isMembersLoading,
    error: (orgError as Error) || (membersError as Error) || null,
    refetchOrganization,
    refetchMembers,
    isOwner,
    isAdmin,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider"
    );
  }
  return context;
};